import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const DialysisTechnologyCoursePage: React.FC = () => {
  const dialysisData = {
    title: "Bachelor of Science in Dialysis Technology",
    shortName: "B.Sc DT",
    duration: "3 Years",
    description: "A specialized healthcare program focusing on renal replacement therapy, dialysis procedures, and care of patients with kidney diseases and renal failure.",
    overview: "B.Sc Dialysis Technology prepares students to become dialysis technologists who operate dialysis machines and provide renal replacement therapy for patients with kidney failure. The program covers nephrology, dialysis procedures, vascular access, water treatment systems, and patient monitoring. Students learn to manage hemodialysis and peritoneal dialysis, maintain equipment, and provide specialized care for chronic and acute kidney disease patients.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Biology",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Interest in nephrology and medical technology",
      "Good attention to detail",
      "Empathy for chronically ill patients"
    ],
    careerProspects: {
      roles: [
        "Dialysis Technologist",
        "Hemodialysis Technician",
        "Peritoneal Dialysis Specialist",
        "Renal Care Coordinator",
        "Dialysis Equipment Specialist",
        "Vascular Access Coordinator",
        "Home Dialysis Trainer",
        "Quality Assurance Specialist",
        "Nephrology Research Assistant",
        "Dialysis Unit Manager"
      ],
      industries: [
        "Dialysis Centers",
        "Nephrology Hospitals",
        "Kidney Transplant Centers",
        "Home Healthcare Services",
        "Medical Equipment Companies",
        "Research Institutions",
        "Government Health Services",
        "Private Nephrology Clinics",
        "Pharmaceutical Companies",
        "Insurance Companies"
      ],
      averageSalary: "₹3-7 LPA (varies by experience and location)",
      topRecruiters: [
        "NephroPlus",
        "Fresenius Medical Care",
        "Apollo Dialysis Centers",
        "Fortis Healthcare",
        "AIIMS Nephrology",
        "Manipal Hospitals",
        "Max Healthcare",
        "Government Hospitals",
        "Baxter Healthcare",
        "B. Braun Medical"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy and Physiology",
          "Renal Anatomy and Physiology",
          "Basic Sciences",
          "Medical Terminology",
          "Microbiology and Infection Control",
          "Patient Care Fundamentals",
          "Communication Skills",
          "Introduction to Nephrology"
        ]
      },
      {
        year: 2,
        subjects: [
          "Dialysis Principles",
          "Hemodialysis Technology",
          "Peritoneal Dialysis",
          "Renal Pathophysiology",
          "Vascular Access",
          "Water Treatment Systems",
          "Dialysis Medications",
          "Patient Monitoring"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Dialysis Procedures",
          "Continuous Renal Replacement Therapy",
          "Home Dialysis Programs",
          "Quality Management",
          "Emergency Procedures",
          "Research Methods",
          "Professional Practice",
          "Clinical Internship"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc Dialysis Technology",
      "Entrance examination or merit-based selection",
      "Document verification",
      "Medical fitness certificate",
      "Fee payment and admission",
      "Orientation program",
      "Begin theoretical and practical training",
      "Clinical rotations in dialysis units"
    ],
    topColleges: [
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹15,000 PA",
        ranking: "Premier Medical Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹3 LPA",
        ranking: "Premier Medical College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹3.5 LPA",
        ranking: "Deemed University"
      },
      {
        name: "JSS Academy of Higher Education",
        location: "Mysore, Karnataka",
        fees: "₹2.5 LPA",
        ranking: "Deemed University"
      },
      {
        name: "Government Medical College",
        location: "Mumbai, Maharashtra",
        fees: "₹40,000 PA",
        ranking: "Government Medical College"
      }
    ],
    skills: [
      "Equipment Operation",
      "Technical Proficiency",
      "Patient Care",
      "Infection Control",
      "Problem Solving",
      "Attention to Detail",
      "Communication Skills",
      "Emergency Response",
      "Quality Control",
      "Teamwork",
      "Compassion",
      "Continuous Learning"
    ],
    futureScope: "Dialysis technology is advancing with artificial kidneys, wearable dialysis devices, home dialysis expansion, and artificial intelligence in treatment optimization. The increasing prevalence of diabetes and hypertension leading to kidney disease ensures strong demand. Future opportunities include specialized areas like pediatric dialysis, acute dialysis in ICUs, and emerging therapies like peritoneal dialysis automation.",
    faqs: [
      {
        question: "What is the role of a dialysis technologist?",
        answer: "Dialysis technologists operate dialysis machines, monitor patients during treatment, maintain equipment, ensure water quality, manage vascular access, and provide education to patients and families about dialysis procedures."
      },
      {
        question: "Is working with dialysis patients emotionally challenging?",
        answer: "Yes, dialysis patients often have chronic conditions requiring long-term treatment. However, helping patients maintain quality of life and providing life-sustaining treatment is highly rewarding. Training includes coping strategies and emotional support."
      },
      {
        question: "What are the different types of dialysis?",
        answer: "Main types include hemodialysis (blood cleaned outside the body), peritoneal dialysis (using the peritoneal membrane), and continuous renal replacement therapy (CRRT) for critically ill patients."
      },
      {
        question: "Can I advance to supervisory roles?",
        answer: "Yes, career advancement includes becoming charge technologists, unit supervisors, training coordinators, quality assurance managers, or dialysis facility administrators with additional experience and education."
      },
      {
        question: "What is the job market like for dialysis technologists?",
        answer: "The job market is strong due to increasing kidney disease prevalence, aging population, and growing number of dialysis centers. There's particularly high demand in underserved areas and for home dialysis programs."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {dialysisData.title} ({dialysisData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={dialysisData} />
      </div>
    </div>
  );
};

export default DialysisTechnologyCoursePage;